import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './ReactionThreadDesktop.module.scss';
import ThreadReactionList from './ThreadReactionList';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import ReactionThreadMenu from './ReactionThreadMenu';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { EditorActions } from '../../editor/editorSlice';
import { isTouchDevice } from '../../constants/os';

const POST_FRAGMENT = gql(`
  fragment ReactionThreadDesktop_post on RootPostFields {
    id
    ...ReactionThreadMenu_post
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment ReactionThreadDesktop_thread on PublicThreadFields {
    id
    ...ThreadReactionList_thread
    ...ReactionThreadMenu_thread
  }
`);

const REACTION_AVATAR_WIDTH = 34;
const OVERLAP = parseInt(styles.overlap, 10);

interface ReactionThreadDesktopProps {
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  expanded: boolean;
  size: MagneticWallItemSize;
}

const ReactionThreadDesktop = ({
  thread,
  post,
  expanded,
  size,
}: ReactionThreadDesktopProps) => {
  const dispatch = useAppDispatch();
  const [isHovered, setIsHovered] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

  useEffect(() => {
    if (isHovered) {
      dispatch(EditorActions.highlightReaction(thread.id));
    } else {
      dispatch(EditorActions.removeMagneticWallHighlight());
    }
  }, [dispatch, thread.id, isHovered]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [maxItemsShown, setMaxItemsShown] = useState<number | undefined>(
    size === 'compact' ? 3 : undefined,
  );

  useLayoutEffect(() => {
    const containerEl = containerRef.current;

    if (size === 'compact') {
      setMaxItemsShown(3);
      return undefined;
    }

    if (containerEl == null) return undefined;

    const handleResize = () => {
      const { width } = containerEl.getBoundingClientRect();
      setMaxItemsShown(Math.round(width / (REACTION_AVATAR_WIDTH - OVERLAP)));
    };
    handleResize();

    const observer = new ResizeObserver(handleResize);
    observer.observe(containerEl);

    return () => {
      observer.disconnect();
    };
  }, [size]);

  useEffect(() => {
    if (isEmojiPickerOpen) {
      dispatch(
        EditorActions.focusReaction({
          id: thread.id,
          isActive: true,
          hasExpandIntent: false,
        }),
      );
    }
  }, [dispatch, thread.id, isEmojiPickerOpen]);

  const hasMoreThanOneReactions = thread.reactions.length > 1;

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, styles[size], {
        [styles.expanded]: expanded,
        [styles.hasMoreThanOneReactions]: hasMoreThanOneReactions,
      })}
    >
      <div className={styles.innerContainer}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          {...(isTouchDevice
            ? {}
            : {
                onMouseEnter: () => setIsHovered(true),
                onMouseLeave: () => setIsHovered(false),
              })}
        >
          <ThreadReactionList
            hovered={isHovered}
            expanded={expanded}
            thread={thread}
            withinOverlay={expanded}
            isEmojiPickerOpen={isEmojiPickerOpen}
            onEmojiPickerOpenChange={setIsEmojiPickerOpen}
            maxItemsShown={maxItemsShown}
            renderMenuButton={(button) => (
              <ReactionThreadMenu thread={thread} post={post} button={button} />
            )}
            spacing={
              size === 'full' &&
              (expanded || isHovered) &&
              (maxItemsShown == null || maxItemsShown > 3)
                ? 'loose'
                : 'tight'
            }
          />
        </div>
      </div>
    </div>
  );
};

export default ReactionThreadDesktop;
